import React from "react";
import {
  OBJECTIVE_TYPE,
  REVIEW_STATUS,
} from "../../_config/quarterlyOkrConfig";
import {
  ContributorStack,
  LinkChip,
  PersonInline,
} from "../../_shared/QokrPrimitives/QokrPrimitives";
import QokrOkrDetails from "../../_shared/QokrOkrDetails/QokrOkrDetails";
import "./_live-objective-row.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One objective in the live review — EXW's live-review row (`eblr-row*`,
// `eblr-act-btn*`, `eblr-status-pill`, `eblr-transcript*`), so this session
// looks and behaves like every other live review the CEO and coach sit in
// (EXW, Strategy Canvas, Annual OKRs). Header: statement, link icon, verdict;
// body: owner and contributors, key results and initiatives, the coach's
// comments, then the editors' actions. Members get it without the actions.
//
// Actions, as in EXW — there is no "To discuss" button in the room:
//   • to discuss → Accept (accepts and marks it discussed in one click)
//   • accepted with a coach comment not yet discussed → Mark discussed
//   • always → Modify, Remove
//
// Icons and the status pill are EXW's own (`icons` prop — each app passes its
// set: the CEO app's EXW `_design/icons`, the coach app's Strategy Canvas
// helpers), so the marks match every other live review exactly.
// ═══════════════════════════════════════════════════════════════════════════

// Something to notice before deciding (§9.3 flags carried into the session).
const flagFor = (objective) => {
  if (objective.owner?.is_deactivated)
    return "Owner is deactivated — pick a new owner";
  if (objective.member?.is_deactivated) return "This person is deactivated";
  if (objective.accepted_by_member === false)
    return "Not accepted by the member";
  return null;
};

const LiveObjectiveRow = ({
  type,
  objective,
  linkLabel = null,
  expanded,
  onToggle,
  canEdit,
  busy,
  comments = [],
  onAccept,
  onDiscussed,
  onEdit,
  onDelete,
  icons: {
    StatusPill,
    CheckIcon,
    EditIcon,
    TrashIcon,
    ChevronUpIcon,
    ChevronDownIcon,
  },
}) => {
  const accepted = objective.review?.status !== REVIEW_STATUS.TO_DISCUSS;
  const discussed = Boolean(objective.review?.discussed_at);
  const flag = flagFor(objective);
  const isCompany = type === OBJECTIVE_TYPE.COMPANY;

  return (
    <div
      className={`eblr-row qokr-lr-row${busy ? " is-pending" : ""}`}
      data-live-target={`${type}:${objective.objective_id}`}
    >
      <button type="button" className="eblr-row-header" onClick={onToggle}>
        <span className="eblr-row-left">
          <span className="eblr-row-label">
            {objective.objective || "Untitled objective"}
          </span>
        </span>
        <span className="eblr-row-right">
          <LinkChip
            label={isCompany ? objective.annual_okr : linkLabel}
            kind={isCompany ? "company" : "individual"}
            relationship={objective.relationship}
            focusable={false}
          />
          <StatusPill
            accepted={accepted}
            className="eblr-status-pill"
            acceptedClassName="eblr-status-accepted"
            discussClassName="eblr-status-discuss"
          />
          <span className="eblr-row-chevron">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="eblr-row-body">
          <div className="cob__row-meta qokr-lr-row__meta">
            <PersonInline person={objective.owner || objective.member} />
            <ContributorStack people={objective.contributors || []} />
          </div>

          {flag ? (
            <p className="qokr-lr-row__flag">
              <i className="fa-solid fa-flag" aria-hidden="true" />
              {flag}
            </p>
          ) : null}

          <QokrOkrDetails
            keyResults={objective.key_results || []}
            initiatives={objective.initiatives || []}
          />

          {comments.length ? (
            <div className="eblr-transcripts">
              {comments.map((comment) => (
                <div className="eblr-transcript" key={comment.coach_comment_id}>
                  <span className="eblr-transcript-badge">Coach comment</span>
                  <p>{comment.comment_text}</p>
                  {discussed ? (
                    <span className="eblr-discussed-tag">
                      <CheckIcon size={11} /> Discussed
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {canEdit ? (
            <div className="eblr-row-actions">
              {!accepted ? (
                <button
                  type="button"
                  className="eblr-act-btn is-accept"
                  disabled={busy}
                  onClick={onAccept}
                >
                  <CheckIcon size={12} /> {busy ? "Accepting…" : "Accept"}
                </button>
              ) : null}
              {accepted && comments.length && !discussed ? (
                <button
                  type="button"
                  className="eblr-act-btn is-discussed"
                  disabled={busy}
                  onClick={onDiscussed}
                >
                  <CheckIcon size={12} /> Mark discussed
                </button>
              ) : null}
              <button
                type="button"
                className="eblr-act-btn is-modify"
                disabled={busy}
                onClick={onEdit}
              >
                <EditIcon size={12} /> Modify
              </button>
              <button
                type="button"
                className="eblr-act-btn is-remove"
                disabled={busy}
                onClick={onDelete}
              >
                <TrashIcon size={12} /> Remove
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default LiveObjectiveRow;
